import React, { useLayoutEffect, useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Popover, Card, Typography } from "@material-ui/core";
import _ from "lodash";
import { Subject, from, of, merge } from "rxjs";
import { filter, tap, mergeMap, map, catchError } from "rxjs/operators";

import { TableContext } from "./TableContext";
import { TableActionTypes, IAction, IColumn } from "./interfaces";
import { parseCell } from "./utils";
import { Button } from "../../components";

const Item: React.FC<{
  value: any;
  fullWidth?: boolean;
  label: string;
  id: string;
}> = ({ value, id, label, fullWidth = false }) => {
  return (
    <div
      key={id}
      style={{
        display: "flex",
        flexDirection: "column",
        width: fullWidth ? "100%" : "50%"
      }}
    >
      <Typography color="textSecondary" variant="caption">
        {label}
      </Typography>
      <Typography>{value}</Typography>
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  root: {},
  card: {
    maxWidth: "450px",
    padding: theme.spacing(2)
  },
  header: {
    marginBottom: theme.spacing(2),
    wordBreak: "break-word"
  },
  button: {
    margin: theme.spacing(1)
  },
  content: {
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "space-between"
  },
  buttonWrapper: {
    display: "flex",
    flexFlow: "row wrap",
    justifyContent: "center",
    marginTop: theme.spacing(2),
    width: "100%"
  }
}));

type DetailAction = {
  label: string;
  shouldDisable?: (row: any) => boolean;
  shouldHide?: (row: any) => boolean;
  onClick: (row: any) => void;
};

type Props = {
  title?: (row: any) => any;
  detailCardActions?: DetailAction[];
  getData?: (row: any) => Promise<any>;
  columns: IColumn[];
};

const INIT_STATE = {
  loading: false,
  error: null,
  detail: null
};

export const TableDetailCard: React.FC<Props> = ({
  title,
  detailCardActions,
  getData,
  columns
}) => {
  const classes = useStyles();
  const [position, setPosition] = useState<any>(null);

  const [state, setState] = useState<{
    detail: any;
    loading: boolean;
    error: any;
  }>({
    ...INIT_STATE
  });

  const { subscribeAction } = useContext(TableContext);

  useLayoutEffect(() => {
    const tableAction$ = new Subject<IAction>();
    const detailSubscription = tableAction$
      .pipe(
        filter(action => action.type === TableActionTypes.onRowClick),
        tap(action => {
          if (action.payload.position) setPosition(action.payload.position);
        }),
        mergeMap(action => {
          const data$ = getData
            ? from(getData(action.payload.row))
            : of(action.payload.row);

          return merge(
            of({
              detail: null,
              error: null,
              loading: true
            }),
            data$.pipe(
              map(data => ({
                loading: false,
                error: null,
                detail: data
              })),
              catchError(error =>
                of({
                  detail: action.payload.row,
                  loading: false,
                  error
                })
              )
            )
          );
        })
      )
      .subscribe(newState => {
        setState(newState);
      });

    const subscription = subscribeAction(tableAction$);

    return () => {
      tableAction$.complete();
      subscription.unsubscribe();
      detailSubscription.unsubscribe();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state.detail) return null;

  return (
    <Popover
      anchorPosition={position}
      anchorReference="anchorPosition"
      onClose={() => {
        setPosition(null);
        setState({
          ...INIT_STATE
        });
      }}
      open
      transformOrigin={{
        vertical: "top",
        horizontal: "center"
      }}
    >
      <Card className={classes.card}>
        {title && (
          <Typography className={classes.header} variant="h5">
            {title(state.detail)}
          </Typography>
        )}
        <div className={classes.content}>
          {columns
            .filter(c => !_.get(c.meta, "hideInDetail"))
            .map(c => {
              const renderItem = _.get(c.meta, "renderItem");
              if (renderItem)
                return renderItem(state.detail[c.id], state.detail);

              return (
                <Item
                  label={c.label}
                  id={c.id}
                  key={c.id}
                  fullWidth={_.get(c.meta, "fullWidth")}
                  value={
                    !state.detail[c.id] ? "" : parseCell(state.detail[c.id], c)
                  }
                />
              );
            })}
        </div>
        <div className={classes.buttonWrapper}>
          {detailCardActions &&
            detailCardActions
              .filter(action => {
                if (!action.shouldHide) {
                  return true;
                }
                return !action.shouldHide(state.detail);
              })
              .map(action => (
                <Button
                  id="action"
                  key={action.label}
                  color="primary"
                  disabled={
                    action.shouldDisable
                      ? action.shouldDisable(state.detail)
                      : false
                  }
                  className={classes.button}
                  onClick={() => action.onClick(state.detail)}
                >
                  {action.label}
                </Button>
              ))}
          <Button
            id="cancel"
            className={classes.button}
            onClick={() => {
              setPosition(null);
              setState({
                ...INIT_STATE
              });
            }}
          >
            Cancel
          </Button>
        </div>
      </Card>
    </Popover>
  );
};
